import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const WalletOverviewPanel = () => {
  const [walletData, setWalletData] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const fetchWalletData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Get platform wallet (admin/platform role)
      const { data: { user } } = await supabase?.auth?.getUser();

      // Fetch all wallets to compute platform balance
      const { data: wallets } = await supabase
        ?.from('wallets')
        ?.select('id, wallet_role, currency')
        ?.eq('wallet_role', 'platform')
        ?.limit(1);

      let platformBalance = 0;
      let platformWalletId = null;

      if (wallets?.length > 0) {
        platformWalletId = wallets?.[0]?.id;
        const { data: balData } = await supabase?.rpc('get_wallet_balance', { p_wallet_id: platformWalletId });
        platformBalance = parseFloat(balData) || 0;
      }

      // Total balance across all wallets (sum of all completed credits - debits)
      const { data: totalData } = await supabase
        ?.from('wallet_transactions')
        ?.select('ledger_type, amount')
        ?.eq('status', 'completed');

      const totalCredits = (totalData || [])
        ?.filter(t => t?.ledger_type === 'credit')
        ?.reduce((s, t) => s + parseFloat(t?.amount || 0), 0);
      const totalDebits = (totalData || [])
        ?.filter(t => t?.ledger_type === 'debit')
        ?.reduce((s, t) => s + parseFloat(t?.amount || 0), 0);

      // Pending payouts
      const { data: payouts } = await supabase
        ?.from('payout_requests')
        ?.select('*')
        ?.eq('status', 'pending')
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      const pendingTotal = (payouts || [])?.reduce((s, p) => s + parseFloat(p?.amount || 0), 0);

      // Recent transactions (last 5)
      const { data: txns } = await supabase
        ?.from('wallet_transactions')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(5);

      setWalletData({
        platformBalance,
        totalNetFlow: totalCredits - totalDebits,
        totalCredits,
        totalDebits,
        pendingPayoutsCount: (payouts || [])?.length,
        pendingPayoutsTotal: pendingTotal,
        platformWalletId,
        adminUserId: user?.id,
      });
      setPendingPayouts(payouts || []);
      setRecentTransactions(txns || []);
    } catch (err) {
      setError('Failed to load wallet data');
      console.error('WalletOverviewPanel error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  const handleApprove = async (requestId) => {
    setApproving(requestId);
    setError('');
    setSuccessMsg('');
    try {
      const { data, error: rpcError } = await supabase?.rpc('approve_payout', {
        p_request_id: requestId,
      });
      if (rpcError) throw new Error(rpcError?.message);
      setSuccessMsg('Payout approved successfully');
      await fetchWalletData();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to approve payout');
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (requestId) => {
    setError('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      await supabase?.from('payout_requests')?.update({
        status: 'rejected',
        admin_id: user?.id,
        processed_at: new Date()?.toISOString(),
      })?.eq('id', requestId);
      await fetchWalletData();
    } catch (err) {
      setError('Failed to reject payout');
    }
  };

  const formatCurrency = (amount) => `KES ${parseFloat(amount || 0)?.toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const formatTime = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts)?.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 24) return `${hrs}h ago`;
    return new Date(ts)?.toLocaleDateString();
  };

  const getCategoryColor = (cat) => {
    const map = {
      ride_payment: 'text-success', delivery_payment: 'text-success',
      commission: 'text-secondary', withdrawal: 'text-error',
      refund: 'text-warning', bonus: 'text-accent',
      adjustment: 'text-warning', escrow_hold: 'text-text-muted',
      escrow_release: 'text-success',
    };
    return map?.[cat] || 'text-text-muted';
  };

  if (loading) {
    return (
      <div className="card space-y-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-text-primary">Wallet Overview</h3>
        </div>
        {[1,2,3]?.map(i => (
          <div key={i} className="h-12 bg-surface-hover rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Wallet Overview</h3>
          <button
            onClick={fetchWalletData}
            className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors"
            title="Refresh"
          >
            <Icon name="RefreshCw" size={14} />
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-error-50 border border-error-200 mb-3">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}
        {successMsg && (
          <div className="p-3 rounded-lg bg-success-50 border border-success-200 mb-3">
            <p className="text-sm text-success">{successMsg}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 bg-primary-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="Wallet" size={14} color="var(--color-primary)" />
              <span className="text-xs font-medium text-primary">Platform Balance</span>
            </div>
            <p className="text-lg font-bold text-text-primary font-mono">
              {formatCurrency(walletData?.platformBalance)}
            </p>
          </div>
          <div className="p-3 bg-warning-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="Clock" size={14} color="var(--color-warning)" />
              <span className="text-xs font-medium text-warning">Pending Payouts</span>
            </div>
            <p className="text-lg font-bold text-text-primary font-mono">
              {walletData?.pendingPayoutsCount || 0}
            </p>
            <p className="text-xs text-text-muted">{formatCurrency(walletData?.pendingPayoutsTotal)}</p>
          </div>
          <div className="p-3 bg-success-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="TrendingUp" size={14} color="var(--color-success)" />
              <span className="text-xs font-medium text-success">Total Credits</span>
            </div>
            <p className="text-lg font-bold text-text-primary font-mono">
              {formatCurrency(walletData?.totalCredits)}
            </p>
          </div>
          <div className="p-3 bg-error-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="TrendingDown" size={14} color="var(--color-error)" />
              <span className="text-xs font-medium text-error">Total Debits</span>
            </div>
            <p className="text-lg font-bold text-text-primary font-mono">
              {formatCurrency(walletData?.totalDebits)}
            </p>
          </div>
        </div>

        {/* Recent Transactions */}
        <div>
          <h4 className="text-sm font-medium text-text-primary mb-2">Recent Transactions</h4>
          {recentTransactions?.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-3">No transactions yet</p>
          ) : (
            <div className="space-y-2">
              {recentTransactions?.map(tx => (
                <div key={tx?.id} className="flex items-center justify-between p-2 bg-surface-hover rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${tx?.ledger_type === 'credit' ? 'bg-success' : 'bg-error'}`} />
                    <div>
                      <p className={`text-xs font-medium capitalize ${getCategoryColor(tx?.category)}`}>
                        {tx?.category?.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-text-muted">{formatTime(tx?.created_at)}</p>
                    </div>
                  </div>
                  <span className={`text-sm font-semibold font-mono ${tx?.ledger_type === 'credit' ? 'text-success' : 'text-error'}`}>
                    {tx?.ledger_type === 'credit' ? '+' : '-'}{formatCurrency(tx?.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Payout Approval Queue */}
      {pendingPayouts?.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-text-primary">Payout Queue</h3>
            <span className="px-2 py-0.5 bg-warning-50 text-warning text-xs font-medium rounded-full">
              {pendingPayouts?.length} pending
            </span>
          </div>
          <div className="space-y-3">
            {pendingPayouts?.map(req => (
              <div key={req?.id} className="p-3 border border-border rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      {req?.account_name || 'Driver'}
                    </p>
                    <p className="text-xs text-text-muted">
                      {req?.bank_name || 'Bank transfer'} · {formatTime(req?.created_at)}
                    </p>
                  </div>
                  <span className="text-base font-bold text-text-primary font-mono">
                    {formatCurrency(req?.amount)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprove(req?.id)}
                    disabled={approving === req?.id}
                    className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-success-50 text-success hover:bg-success-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                  >
                    {approving === req?.id ? (
                      <><Icon name="Loader" size={12} />Processing...</>
                    ) : (
                      <><Icon name="Check" size={12} />Approve</>
                    )}
                  </button>
                  <button
                    onClick={() => handleReject(req?.id)}
                    disabled={approving === req?.id}
                    className="flex-1 py-1.5 text-xs font-medium rounded-lg bg-error-50 text-error hover:bg-error-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                  >
                    <Icon name="X" size={12} />Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletOverviewPanel;
